import React, { forwardRef } from 'react'
import { Input, InputGroup, InputGroupText, InputGroupTextProps } from './'
import { Label } from './Label'
import { Icon } from '../elements/Icon'
import classNames from 'classnames'
import { IconName } from '@fortawesome/fontawesome-svg-core'
interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    name: string
    label?: string
    isValid?: boolean
    leftIcon?: IconName
    leftIconProps?: InputGroupTextProps
    leftIconColor?: string
    leftElement?: React.ReactNode
    rightElement?: React.ReactNode
    rightIcon?: IconName
    rightIconProps?: InputGroupTextProps
    rightIconColor?: string
    required?: boolean
    hint?: string
    className?: string
    inputClassName?: string
    groupClassName?: string
    error?: string
    delay?: boolean | number
    feedback?: string
    parentProps?: React.HTMLAttributes<HTMLElement>
}
const FormInput = forwardRef<HTMLInputElement, FormInputProps>(({
    name,
    label,
    isValid,
    leftIcon,
    leftIconColor,
    leftIconProps,
    rightIcon,
    rightIconColor,
    rightIconProps,
    leftElement,
    rightElement,
    required = false,
    hint,
    className,
    inputClassName,
    groupClassName,
    error,
    feedback,
    type = 'text',
    value,
    placeholder,
    disabled,
    onChange,
    size,
    parentProps,
    ...otherProps
}, ref) => {
    const renderIcon = (
        icon: IconName,
        iconColor: string,
        iconProps: any
    ) =>
        icon && (
            <InputGroupText
                className={classNames({
                    'text-bg-success border-success':
                            isValid !== undefined && value && isValid,
                    'text-bg-danger border-danger':
                            isValid !== undefined && value && !isValid
                })}
                {...iconProps}
            >
                <Icon name={icon} fixedWidth />
            </InputGroupText>
        )

    const inputId = otherProps.id ? otherProps.id : `${name}-input`

    return (
        <div className={classNames(className, 'mb-2')} {...parentProps}>
            {label && (
                <div className="d-flex justify-content-between mb-1" >
                    <Label
                        text={label}
                        htmlFor={inputId}
                        required={required}
                        hint={hint}
                        bold
                    />
                </div>
            )}
            <InputGroup className={groupClassName}>
                {leftElement}
                {leftIcon && renderIcon(leftIcon, leftIconColor, leftIconProps)}
                <Input
                    ref={ref}
                    id={inputId}
                    name={name}
                    type={type}
                    value={value}
                    className={inputClassName}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={onChange}
                    aria-label={placeholder ? placeholder : label}
                    aria-invalid={error ? 'true' : 'false'}
                    {...otherProps}
                    aria-describedby={`${otherProps['aria-describedby']??''} ${error ? name+'-error' : ''} ${feedback ? name+'-feedback' : ''}`}
                />
                {rightIcon &&
                        renderIcon(rightIcon, rightIconColor, rightIconProps)}
                {rightElement}
            </InputGroup>
            {error && (
                <div
                    id={`${name}-error`}
                    className="invalid-feedback d-block"
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </div>
            )}
            {feedback && (
                <div
                    id={`${name}-feedback`}
                    className="invalid-feedback d-block"
                    role="status"
                    aria-live="polite"
                >
                    {feedback}
                </div>
            )}
        </div>
    )
}
)
FormInput.displayName = 'FormInput'
export { FormInput }
